/**
 * CompanySetupInputsTreeNode — Renders one country/region/authority row.
 *
 * The tree is now rendered FLAT (non-recursive) by the parent — this
 * component only draws a single row + the "children container" when
 * expanded. The parent decides what goes inside.
 *
 * (iter-220, Feb 2026) — Recursive JSX with a self-reference tripped a
 * RangeError in CRA's babel-loader plugin chain, so the recursion was
 * moved out of this module.
 */
import React from 'react';
import {
  ChevronDown, ChevronLeft, Plus, Edit3, Trash2, ToggleLeft, ToggleRight,
  MapPin, Landmark, Globe,
} from 'lucide-react';

const LEVEL_META = {
  country:   { color: '#10B981', icon: Globe },
  region:    { color: '#3B82F6', icon: MapPin },
  authority: { color: '#F5A55A', icon: Landmark },
};

export default function TreeNode(props) {
  const { node, level, expanded, onToggle, onAddChild, onEdit, onDelete, onToggleActive, isAr, children } = props;
  const meta = LEVEL_META[level];
  const Icon = meta.icon;
  const nodeChildren = level === 'country'
    ? (node.regions || [])
    : level === 'region'
      ? (node.authorities || [])
      : [];
  const canExpand = level !== 'authority';
  const isOpen = expanded[node.id];
  const childLevel = level === 'country' ? 'region' : level === 'region' ? 'authority' : null;

  const containerCls = 'rounded-xl border ' + (node.is_active
    ? 'border-white/10 bg-[#151530]'
    : 'border-red-500/20 bg-[#150f0f] opacity-70');

  const countLabel = nodeChildren.length > 0 && level !== 'authority'
    ? (isAr
        ? nodeChildren.length + ' ' + (level === 'country' ? 'إمارة' : 'جهة')
        : nodeChildren.length + ' ' + (level === 'country' ? 'regions' : 'authorities'))
    : null;

  const displayName = isAr ? (node.name_ar || node.name_en) : (node.name_en || node.name_ar);
  const subName = isAr ? (node.name_en || '—') : (node.name_ar || '—');

  return (
    <div className={containerCls}>
      <div className="flex items-center gap-3 p-4">
        {canExpand ? (
          <button
            onClick={() => onToggle(node.id)}
            className="text-[#71717A] hover:text-white p-1"
            data-testid={'toggle-' + node.id}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        ) : (
          <div className="w-6" />
        )}

        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: meta.color + '20', border: '1px solid ' + meta.color + '44' }}
        >
          <Icon className="h-5 w-5" style={{ color: meta.color }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-white font-semibold truncate">{displayName}</div>
            {node.code ? (
              <span className="text-xs text-[#71717A] font-mono bg-black/40 px-2 py-0.5 rounded">
                {node.code}
              </span>
            ) : null}
          </div>
          <div className="text-xs text-[#71717A] truncate">
            {subName}
            {countLabel ? <span className="ms-2 text-[#606060]">· {countLabel}</span> : null}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {childLevel ? (
            <button
              onClick={() => onAddChild(childLevel, node.id)}
              className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-emerald-500/20 transition-colors"
              title={isAr ? 'إضافة ' + (childLevel === 'region' ? 'إمارة' : 'جهة') : 'Add ' + childLevel}
              data-testid={'add-child-' + node.id}
            >
              <Plus className="h-4 w-4 text-emerald-400" />
            </button>
          ) : null}
          <button
            onClick={() => onToggleActive(node)}
            className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-blue-500/20 transition-colors"
            title={node.is_active ? (isAr ? 'إيقاف' : 'Disable') : (isAr ? 'تفعيل' : 'Enable')}
          >
            {node.is_active
              ? <ToggleRight className="h-4 w-4 text-emerald-400" />
              : <ToggleLeft className="h-4 w-4 text-red-400" />}
          </button>
          <button
            onClick={() => onEdit(level, node)}
            className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-amber-500/20 transition-colors"
            title={isAr ? 'تعديل' : 'Edit'}
          >
            <Edit3 className="h-4 w-4 text-amber-400" />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
            title={isAr ? 'حذف' : 'Delete'}
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </button>
        </div>
      </div>

      {isOpen && canExpand ? (
        <div className="ps-8 pe-4 pb-4 space-y-2 border-t border-white/5 pt-3">
          {nodeChildren.length === 0 && !children ? (
            <div className="text-xs text-[#71717A] py-2 ps-2">
              {isAr ? '— لا يوجد بعد. اضغط ➕ لإضافة.' : '— none yet. Click ➕ to add.'}
            </div>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}
